/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

function DeleteVehicleConfirmationDialog({
  open,
  onClose,
  onConfirm,
  vehicleToDelete,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#333",
          }}
        >
          Delete Vehicle
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#666",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "0 24px 24px 24px" }}>
        <Typography sx={{ color: "#333", fontSize: "14px", marginBottom: "16px" }}>
          You are about to delete a vehicle from the listing.
        </Typography>
        
        {vehicleToDelete && (
          <Box sx={{ 
            display: "flex", 
            gap: "16px", 
            marginBottom: "16px",
            padding: "12px",
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
            backgroundColor: "#f9f9f9"
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#333", marginBottom: "4px" }}>
                Vehicle ID
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#333" }}>
                {vehicleToDelete.id || "N/A"}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#333", marginBottom: "4px" }}>
                Friendly Name
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#333" }}>
                {vehicleToDelete.description || "N/A"}
              </Typography>
            </Box>
          </Box>
        )}
        
        <Typography sx={{ color: "#333", fontSize: "14px", marginBottom: "8px" }}>
          This vehicle will be removed from the vehicles list. Are you sure you want to continue?
        </Typography>
        <Typography sx={{ color: "#666", fontSize: "14px" }}>
          That action can not be undone.
        </Typography>
      </DialogContent>

      <Box sx={{ 
        display: "flex", 
        justifyContent: "flex-end", 
        gap: "8px", 
        padding: "0 24px 24px 24px" 
      }}>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: '#e0e0e0',
            color: '#333',
            textTransform: 'none',
            borderRadius: '4px',
            px: 3,
            py: 0.5,
            height: '36px',
            border: '2px solid transparent',
            '&:hover': {
              backgroundColor: '#d0d0d0'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            onClose(); // Close dialog first
            onConfirm(); // Then execute delete
          }}
          sx={{
            backgroundColor: 'transparent',
            color: '#b50b00',
            border: '2px solid #f44336',
            textTransform: 'none',
            borderRadius: '4px',
            px: 3,
            py: 0.5,
            height: '36px',
            fontWeight: 500,
            fontSize: '14px',
            '&:hover': {
              backgroundColor: '#f44336',
              color: 'white'
            }
          }}
        >
          Delete
        </Button>
      </Box>
    </Dialog>
  );
}

DeleteVehicleConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  vehicleToDelete: PropTypes.shape({
    id: PropTypes.string,
    description: PropTypes.string,
  }),
};

export default DeleteVehicleConfirmationDialog;
